import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SettingsBloc } from '@kbloc';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { DialogueBloc } from '@kp/dialogue/dialogue.bloc';
import { Dialogue, DialogueLogicKey } from '@kp/dialogue/models/dialogue.model';
import { UserBloc } from '@kp/user/user.bloc';
import { TitleService } from '@kservice';
import { Credentials, Setting, Status } from '@ktypes/models';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-screen-signup-password',
  templateUrl: './screen-signup-password.component.html',
  styleUrls: ['./screen-signup-password.component.scss'],
})
export class ScreenSignupPasswordComponent implements OnInit, OnDestroy {
  private _destroy$ = new Subject<void>();
  private _passwordControl: UntypedFormControl;
  private _title = 'Signup';
  private _userSubmitted = false;

  email: string;
  form: UntypedFormGroup;
  showPassword = false;
  showPasswordReset = false;
  formatError = false;
  stayLoggedIn = true;
  submissionError: string;

  constructor(
    private _authenticationBloc: AuthenticationBloc,
    private _dialogueBloc: DialogueBloc,
    private _router: Router,
    private _settingsBloc: SettingsBloc,
    private _titleService: TitleService,
    private _userBloc: UserBloc
  ) {}

  ngOnInit(): void {
    this._titleService.set(this._title);
    this._passwordControl = new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(/^.{8,}$/),
      Validators.pattern(/[A-Za-z]/),
      Validators.pattern(/\d/),
      Validators.pattern(/^\S+.*\S+$/),
    ]);
    this.form = new FormGroup({
      password: this._passwordControl,
    });
    this._passwordControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      this.formatError =
        value == null ||
        (value?.length > 0 &&
          (this._passwordControl.touched || this._passwordControl.dirty) &&
          this._passwordControl.invalid);
    });

    combineLatest([this._userBloc.passwordChanged$, this._dialogueBloc.dialogue$])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([passwordChangedStatus, dialogueStatus]) => {
        if (passwordChangedStatus?.status === Status.done && passwordChangedStatus.data) {
          // On successful password change, "login" user for Resourceful to get a new refreshToken and clear the "unset" value
          this._loginUserIfRequired();
          // Note: if there are intermittent issues related to login being async and taking too long prior to the cleanup/redirect,
          //       may need to put the following two methods in an authStatus subscription, but didn't seem to need it as of 12/06/22
          this._cleanUpPassword();
          this._navigateBasedOnDialogueStatus(dialogueStatus?.data);
        } else if (passwordChangedStatus?.status === Status.error && !passwordChangedStatus?.data) {
          this.submissionError = this._authenticationBloc.getAuthErrorMessage(
            passwordChangedStatus?.message?.message,
            'There was an error resetting your password. Please try again.'
          );
          this.showPasswordReset = passwordChangedStatus?.message?.message === 'Bad Request';
        }

        // reset user submitted
        if (this._userSubmitted) {
          this._userSubmitted = false;
        }
      });
  }

  private _loginUserIfRequired() {
    // Resourceful needs to "login" user to clear the "unset" RefreshToken and get a new one
    this._authenticationBloc.postRegisterLogin(
      new Credentials().deserialize({ email: this.email, password: this.form?.get('password').value })
    );
  }

  ngOnDestroy() {
    this._cleanUpPassword();
    this._destroy$.next();
    this._destroy$.complete();
  }

  onSubmit(value) {
    if (this._userSubmitted) {
      // only allow the first one through
      return;
    }
    this._userSubmitted = true;
    this._userBloc.setPassword(value?.password);
  }

  passwordChanged() {
    this.formatError = !!this._passwordControl.errors;
    delete this.submissionError;
  }

  toggleShowPassword(event: MouseEvent) {
    // Make sure the button won't submit the form
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.showPassword = !this.showPassword;
  }

  loggedInCheckbox(isChecked: boolean) {
    this.stayLoggedIn = isChecked;
    this._settingsBloc.changeUserSetting(Setting.staySignedIn, this.stayLoggedIn);
  }

  setReturnUrl() {
    this._authenticationBloc.setChangePasswordReturnUrl('');
  }

  getUserEmail(email: string) {
    this.email = email;
  }

  private _cleanUpPassword() {
    // ensure the password is cleared once it is no longer needed
    this._passwordControl?.setValue?.(null);
    this.form?.get('password')?.setValue?.(null);
  }

  private _navigateBasedOnDialogueStatus(dialogue: Dialogue) {
    // advance to the next page
    if (dialogue != null && dialogue?.dialogueBlock != null) {
      // there is a dialogue and current dialogueBloc, get back to the dialogue
      const dialogueRoute =
        dialogue?.logicKey === DialogueLogicKey.dialogue_purposeful_onboarding_v1 ? 'dialogue/onboarding' : 'dialogue';
      void this._router.navigate([dialogueRoute], {
        queryParams: { logicKey: dialogue?.logicKey, returnUrl: '' },
        replaceUrl: true,
      });
    } else {
      // no dialogue, go to root path
      void this._router.navigate([''], { replaceUrl: true });
    }
  }
}
